//
// REFERENCE: https://chonky.io/docs/2.x/
//

import {
    ChonkyActions,
    ChonkyFileActionData,
    FileArray,
    FileBrowserProps,
    FileData,
    FileHelper,
    FullFileBrowser,
    defineFileAction
} from 'chonky'
import React from 'react'
//import { showActionNotification } from '../util';
import DimensionsService from "../../services/DimensionsService"
import Dimension from "../../models/Dimension"
import LocationInfo from "../../models/LocationInfo"

//import './chonky.css';

import { setChonkyDefaults } from 'chonky';
import { ChonkyIconFA } from 'chonky-icon-fontawesome';
setChonkyDefaults({iconComponent: ChonkyIconFA});

// We define a custom interface for file data because we want to add some custom fields
// to Chonky's built-in `FileData` interface.
interface CustomFileData extends FileData {
    parentId: string | null;
    childrenIds: string[];
}

interface CustomFileMap {
    [fileId: string]: CustomFileData;
}

function pathDirname(path: string) {
    let pos = path.lastIndexOf('/');
    return pos >= 0 ? path.substring(0, pos) : '';
}

function pathBasename(path: string) {
    let pos = path.lastIndexOf('/');
    return pos >= 0 ? path.substring(pos + 1) : path;
}

function pathReplaceDir(path: string, targetDir: string) {
    return targetDir ? `${targetDir}/${pathBasename(path)}` : pathBasename(path);
}

function pathReplaceBasename(path: string, targetName: string) {
    let dirName = pathDirname(path);
    return dirName ? `${dirName}/${targetName}` : targetName;
}

function createFilesMap(data: any, root: string, locationsMap: {[key: string] : LocationInfo; }) {
    let map: CustomFileMap = {} as CustomFileMap;
    map[root] = { id: root, name: root, isDir: true, parentId: null, childrenIds: []};

    data.map((item: any) => {
        let dirname = pathDirname(item.name);
        let parent = dirname ? `${root}/${dirname}` : root;
        let id = `${root}/${item.name}`;
        map[id] = {
            id: id,
            name: pathBasename(item.name),
            isDir: item.directory,
            modDate: item.modified,
            parentId: parent,
            childrenIds: []
        }


        if (item.directory) {
            if (parent == 'dimension/locations') {
                let locId = map[id].name
                if (locId in locationsMap) {
                    map[id].name = locationsMap[locId].name
                }
            }
        } else {
            map[id].size = parseInt(item.size);
        }

        if (map[parent]) {
            map[parent].childrenIds.push(id);
        } else {
            console.error(`Unknown file parent [${parent}] for file [${item.name}]`);
        }
    });

    return map;
}

const useFiles = (
    fileMap: CustomFileMap,
    currentFolderId: string
): FileArray => {
    return React.useMemo(() => {
        const currentFolder = fileMap[currentFolderId];
        if (!currentFolder || !currentFolder.childrenIds) {
            return [];
        }
        return currentFolder.childrenIds.map((fileId: string) => fileMap[fileId]);
    }, [currentFolderId, fileMap]);
};

const useFolderChain = (
    fileMap: CustomFileMap,
    currentFolderId: string
): FileArray => {
    return React.useMemo(() => {
        const currentFolder = fileMap[currentFolderId];
        if (!currentFolder) {
            return [];
        }

        const folderChain = [currentFolder];

        let parentId = currentFolder.parentId;
        while (parentId) {
            const parentFile = fileMap[parentId];
            if (parentFile) {
                folderChain.unshift(parentFile);
                parentId = parentFile.parentId;
            } else {
                break;
            }
        }

        return folderChain;
    }, [currentFolderId, fileMap]);
};

async function loadDimensionFile(env: string, dimId: string, file: string, download: boolean)
{
    let res = await DimensionsService.getFile(env, dimId, file);
    if (!res.file_data) {
        console.error(`Null data for file [${file}]`);
        return;
    }

    let fileBlob = new Blob([res.file_data]);
    let a = document.createElement("a");
    let url = URL.createObjectURL(fileBlob);
    a.href = url;
    if (download) {
        a.download = pathBasename(file);
    }
    a.target = 'blank';
    document.body.appendChild(a);
    a.click();
    setTimeout(function() {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }, 0);
}

const RenameAction = defineFileAction({
    id: 'rename_action',
    requiresSelection: true,
    button: {
      name: 'Rename',
      toolbar: false,
      contextMenu: true,
      icon: "text"
    },
});

const EditDimensionFiles: React.FC<{dimension: Dimension}> = React.memo((props) => {
    const env = 'dev';
    const rootName = "dimension";
    const fileActions = [ChonkyActions.CreateFolder,
                         ChonkyActions.UploadFiles,
                         ChonkyActions.DownloadFiles,
                         ChonkyActions.DeleteFiles,
                         RenameAction];
    const actionsToDisable = [ChonkyActions.EnableGridView.id,
                              ChonkyActions.SelectAllFiles.id,
                              ChonkyActions.ClearSelection.id,
                              ChonkyActions.SortFilesBySize.id,
                              ChonkyActions.ToggleHiddenFiles.id];

    const [fileMap, setFileMap] = React.useState({} as CustomFileMap);
    const [currentFolderId, setCurrentFolderId] = React.useState(rootName);

    const files = useFiles(fileMap, currentFolderId);
    const folderChain = useFolderChain(fileMap, currentFolderId);

    function reloadFiles() {
        DimensionsService.listDimensionFiles(env, props.dimension.id)
        .then(data => { setFileMap(createFilesMap(data, rootName, props.dimension.locationsMap)); })
        .catch(err => console.error(`Failed to list dimension files [${props.dimension.id}]: ${err}`));
    }

    function withoutRoot(path: string) {
        return path.substring(rootName.length + 1);
    }
    
    function withRoot(path: string) {
        return `${rootName}/${path}`;
    }
    
    function fileExists(path: string) {
        return withRoot(path) in fileMap;
    }

    function isDirectory(path: string) {
        let item = fileMap[withRoot(path)];
        return item && item.isDir;
    }

/*
    const thumbnailGenerator = useCallback(
        (file: FileData) =>
            file.thumbnailUrl ? `https://chonky.io${file.thumbnailUrl}` : null,
        []
    );
*/
    function openFilesAction(file?: FileData) {
        if (!file) {
            return;
        }
        if(file.isDir) {
            setCurrentFolderId(file.id);
        } else {
            loadDimensionFile(env, props.dimension.id, withoutRoot(file.id), false);
        }
    }

    function deleFilesAction(files: FileData[]) {
        let fileList = files.map(file => withoutRoot(file.id));
        let shortNameList = fileList.map(path => pathBasename(path));
        if (window.confirm(`Do you want to delete file(s): ${JSON.stringify(shortNameList)}`)) {
            DimensionsService.deleteFiles(env, props.dimension.id, fileList).finally(reloadFiles);
        }
    }

    function moveFilesAction(files: FileData[], destItem: FileData) {
        let fileList = files.map(file => withoutRoot(file.id));
        let dest = withoutRoot(destItem.id);

        let existingInDest = [];
        for (const file of fileList) {
            let targetPath = pathReplaceDir(file, dest);
            if (fileExists(targetPath)) {
                if (!isDirectory(file) && isDirectory(targetPath)) {
                    window.alert(`Cannot overwrite existing directory "${pathBasename(targetPath)}" with a file.`);
                    return;
                }
                existingInDest.push(pathBasename(file));
            }
        }
        if (existingInDest.length > 0) {
            if (!window.confirm(`Overwrite existing file(s) in destination: ${JSON.stringify(existingInDest)}`)) {
                return;
            }
        }
        DimensionsService.moveFiles(env, props.dimension.id, fileList, dest).finally(reloadFiles);
    }

    function createFolderAction(currentFolder: string) {
        const folderName = window.prompt('Provide the name for your new folder:');
        if (folderName) {
            let folderPath = pathReplaceDir(folderName, withoutRoot(currentFolder));
            if (fileExists(folderPath)) {
                window.alert(`Folder already exists: ${folderName}`);
                return;
            }
            DimensionsService.createDirectory(env, props.dimension.id, folderPath).finally(reloadFiles);
        }
    }

    function uploadFilesAction(currentFolder: string) {
        let baseFolder = withoutRoot(currentFolder);
        let input = document.createElement('input');
        input.type = 'file';
        input.name = 'file';
        input.multiple = true;

        input.onchange = (e: React.ChangeEvent<HTMLInputElement> | Event) => {
            let event = e as React.ChangeEvent<HTMLInputElement>;
            //@ts-ignore
            if (!event.target.files) {
                return;
            }

            let waitCount = 1;
            const decCounter = () => {
                waitCount -= 1;
                if (waitCount == 0) {
                    reloadFiles();
                }
            }

            let existing = [];
            for (let i = 0; i < event.target.files.length; ++i) {
                let file = event.target.files[i];
                let targetPath = pathReplaceDir(file.name, baseFolder);
                if (file && fileExists(targetPath)) {
                    if (isDirectory(targetPath)) {
                        window.alert(`Cannot overwrite existing directory "${pathBasename(targetPath)}" with a file.`);
                        return;
                    }
                    existing.push(file.name);
                }
            }

            if (existing.length > 0) {
                if (!window.confirm(`Overwrite existing file(s): ${JSON.stringify(existing)}`)) {
                    return;
                }
            }

            for (let i = 0; i < event.target.files.length; ++i) {
                let file = event.target.files[i];
                if (!file) {
                    continue;
                }
                waitCount++;

                console.log(`Uploading file: ${file.name}`);

                let reader = new FileReader();
                reader.onload = function(event: any) {
                    let fullName = `${baseFolder}/${file.name}`;
                    DimensionsService.uploadDimensionFile(env, props.dimension.id, fullName, event.target.result)
                    .finally(decCounter);
                };
                reader.readAsArrayBuffer(file)
            }
            decCounter();
        };
        input.click();
    }

    function downloadFilesAction(files: FileData[]) {
        for (const file of files) {
            if (!file.isDir) {
                loadDimensionFile(env, props.dimension.id, withoutRoot(file.id), true);
            }
        }
    }

    function openSelectionAction(files: FileData[]) {
        for (const file of files) {
            if (!file.isDir) {
                loadDimensionFile(env, props.dimension.id, withoutRoot(file.id), false);
            }
        }
    }

    function renameAction(files: FileData[]) {
        if (files.length != 1) {
            return;
        }
        let selectedPath = withoutRoot(files[0].id);
        let selectedBasename = pathBasename(selectedPath);
        const targetName = window.prompt('Provide new name:', selectedBasename);
        if (!targetName || targetName == selectedBasename) {
            return;
        }
        let fullTargetPath = pathReplaceBasename(selectedPath, targetName);
        if (fileExists(fullTargetPath)) {
            window.alert(`Name "${targetName}" already exists!`);
            return;
        }

        DimensionsService.renameFile(env, props.dimension.id, selectedPath, fullTargetPath).finally(reloadFiles);
    }

    function fileActionHandler(data: ChonkyFileActionData) {
        switch(data.id) {
            case  ChonkyActions.OpenFiles.id: {
                const { targetFile, files } = data.payload;
                openFilesAction(targetFile ?? files[0]);
                break;
            }
            case ChonkyActions.DeleteFiles.id: {
                deleFilesAction(data.state.selectedFilesForAction);
                break;
            }
            case ChonkyActions.MoveFiles.id: {
                moveFilesAction(data.payload.files, data.payload.destination);
                break;
            }
            case ChonkyActions.CreateFolder.id: {
                createFolderAction(currentFolderId);
                break;
            }
            case ChonkyActions.UploadFiles.id: {
                uploadFilesAction(currentFolderId);
                break;
            }
            case ChonkyActions.DownloadFiles.id: {
                downloadFilesAction(data.state.selectedFilesForAction);
                break;
            }
            case ChonkyActions.OpenSelection.id: {
                openSelectionAction(data.state.selectedFilesForAction);
                break;
            }
            case RenameAction.id: {
                renameAction(data.state.selectedFilesForAction);
                break;
            }
            default: console.log(`Unhandled file action ${data.id}`);
        }
    };

    React.useEffect(() => {
        reloadFiles();
    }, [props.dimension.id]);

    return (
        <div style={{ height: "600px" }}>
            <FullFileBrowser
                files={files}
                folderChain={folderChain}
                fileActions={fileActions}
                disableDefaultFileActions={actionsToDisable}
                onFileAction={fileActionHandler}
                //thumbnailGenerator={thumbnailGenerator}
                defaultFileViewActionId='enable_list_view'
            />
        </div>
    );
});

export default EditDimensionFiles;
